import { createContext, ReactNode, useEffect } from "react";
import { LinkType } from "src/contexts/_shared/domain/LinkType";
import { useSearchParams } from "react-router-dom";
import { NotFoundErrorScreen } from "../routes/NotFoundErrorScreen";
import { useBrowserLocalStorage } from "../hooks/useBrowserLocalStorage";

export type SearchParamsContextType = {
  slug: string;
  linkType: LinkType;
  token: string;
};

export const SearchParamsContext = createContext<SearchParamsContextType>({
  slug: "",
  linkType: "" as unknown as LinkType,
  token: "",
});

export const SearchParamsProvider = ({
  children,
}: Partial<SearchParamsContextType> & { children: ReactNode }) => {
  const [searchParams] = useSearchParams();
  const [storedSlug, setStoredSlug] = useBrowserLocalStorage<string | null>(
    `stored_query_param.slug`,
    null,
  );
  const [storedLinkType, setStoredLinkType] = useBrowserLocalStorage<string | null>(
    `stored_query_param.linkType`,
    null,
  );

  const searchParamSlug = searchParams.get("slug");
  const searchParamsLinkType = searchParams.get("linkType");
  const searchParamsToken = searchParams.get("token");

  let slug, linkTypeValue;
  if (!searchParamsToken) {
    slug = searchParamSlug ?? storedSlug ?? "";
    linkTypeValue = searchParamsLinkType ?? storedLinkType ?? null;
  }

  useEffect(() => {
    if (searchParamSlug && searchParamSlug !== storedSlug) {
      setStoredSlug(searchParamSlug);
    }
  }, [searchParamSlug]);

  useEffect(() => {
    if (searchParamsLinkType && searchParamsLinkType !== storedLinkType) {
      setStoredLinkType(searchParamsLinkType);
    }
  }, [searchParamsLinkType]);

  if ((!storedSlug || !storedLinkType) && !searchParamsToken) {
    return <NotFoundErrorScreen />;
  }

  const linkType = linkTypeValue ? new LinkType(linkTypeValue) : null;

  if (!linkType && !searchParamsToken) {
    return <NotFoundErrorScreen />;
  }

  return (
    <SearchParamsContext.Provider
      value={{
        slug: slug ?? "",
        linkType: linkType!,
        token: searchParamsToken ?? "",
      }}
    >
      {children}
    </SearchParamsContext.Provider>
  );
};
